import {ArrowDownIcon, ArrowDownTrayIcon, ArrowUpIcon, TrashIcon} from "@heroicons/react/24/outline";
import React, { useContext, useEffect, useState } from "react";
import Image from "../../Tags/Image";
import GenericModal from "../../GenericModal";
import useModal from "../../GenericModal/useModal";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  deleteEnrichment,
  downloadEnrichListCSV,
  downloadSummarizedEnrichListCSV,
  getEnrichmentList,
} from "../../../apis/enrich";
import { getEnrichmentIntegrations } from "../../../apis/workspace/integrations";
import SharedDataContext from "../../../SharedDataContext";
import { toast } from "react-hot-toast";
import { isEmptyObject, returnArray } from "../../../utils/GenericFn";
import { Tooltip } from "react-tooltip";
import TableLoader from "../../Loaders/TableLoader";
import NoData from "../../NoData";
import Pagination from "../../Pagination";
import EditColumn from "../../EditColumn";
import Filter from "../../Filter";
import PrintDate from "../../Tags/PrintDate";
import ConfirmModal from "../../Modals/ConfirmModal";
import { PencilIcon } from "@heroicons/react/20/solid";
import EditFileName from "../Enrich/EditFileName";
import NavAnchor from "../../Tags/NavAnchor";
import { Button } from "../../ui/button";
import { LucideUploadCloud, Info } from "lucide-react";
import { Badge } from "../../ui/badge";
import TooltipWrapper from "../../TooltipWrapper";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";
import AlertTag from "../../Alerts/Alert";
import UploadWizard from "./UploadWizard";
import PageHeader from "../../PageHeader";
import { useTableData } from "../../../hooks/useTableData";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function EnrichList() {
  const captureEvent = useCaptureEvent();
  const queryClient = useQueryClient();
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};

  const [showWizard, setShowWizard] = useState(false);
  const [currentTab, setCurrentTab] = useState("people");

  // State for handling enrichment list actions
  const [currentEnrichmentId, setCurrentEnrichmentId] = useState("");
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openConfirmModalType, setOpenConfirmModalType] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [noIntegration, setNoIntegration] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [activeIntegrations, setActiveIntegrations] = useState([]);
  const [logos, setLogos] = useState({});

  const { isOpen, openModal, closeModal } = useModal(false);
  const { isOpen: isOpenMap, openModal: openModalMap, closeModal: closeModalMap } = useModal();

  // ✅ Use centralized table data hook
  const {
    data: enrichmentList,
    paginationInfo,
    filterMetadata,
    columnMetadata,
    isLoading,
    isSuccess,
    filters,
    sort,
    columns,
    sortedVisibleColumns,
    getFiltersObject,
    getPageNumber,
    handleSetSort,
    getColumns,
    refetchData,
  } = useTableData({
    queryKey: "get_enrichment_list",
    fetchFn: getEnrichmentList,
    defaultHeaders: {
      "X-Workspace-Id": activeWorkspace?.id,
    },
    restObject: {
      slug: activeWorkspace?.slug,
    },
    enabled: !!activeWorkspace,
  });

  const handleGetEnrichmentList = () => {
    refetchData();
  };


  // delete enrichment list
  const deleteEnrichmentFn = useMutation("delete_enrich", deleteEnrichment, {
    onSuccess(data, variables, context) {
      toast.success(data?.message);
      queryClient.invalidateQueries("get_enrichment_list");
    },
  });

  const handleDeleteEnrichment = () => {
    deleteEnrichmentFn.mutate({
      listId: currentEnrichmentId,
      slug: activeWorkspace?.slug,
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  // download enrich list csv - summarized
  const downloadSummarizedEnrichListCSVFn = useMutation("download_enrich", downloadSummarizedEnrichListCSV, {
    onSuccess(data, variables, context) {
      toast.success("CSV downloaded.");
      queryClient.invalidateQueries("get_enrichment_list");
    },
  });

  const handleDownloadSummarizedEnrichListCSV = (item) => {
    setCurrentEnrichmentId(item.id);
    downloadSummarizedEnrichListCSVFn.mutate(
      {
        listId: item.id,
        slug: activeWorkspace?.slug,
        options: {
          headers: { "X-Workspace-Id": activeWorkspace?.id },
        },
      },
      {
        onSuccess: (data) => {
          // Sanitize the item name to be used as the file name
          const sanitizedFileName = sanitizeFilename("Summarized-" + item.name) + ".csv";

          // Create a URL for the blob
          const fileURL = window.URL.createObjectURL(new Blob([data]));
          // Create a temporary anchor tag
          const fileLink = document.createElement("a");
          fileLink.href = fileURL;
          fileLink.setAttribute("download", sanitizedFileName);
          document.body.appendChild(fileLink);

          fileLink.click();

          fileLink.remove();
          window.URL.revokeObjectURL(fileURL);
        },
      }
    );
  };

  // download enrich list csv
  const downloadEnrichListCSVFn = useMutation("download_enrich", downloadEnrichListCSV, {
    onSuccess(data, variables, context) {
      toast.success(data?.message);
      queryClient.invalidateQueries("get_enrichment_list");
    },
  });

  const handleDownloadEnrichListCSV = (item) => {
    setCurrentEnrichmentId(item.id);
    downloadEnrichListCSVFn.mutate(
      {
        listId: item.id,
        slug: activeWorkspace?.slug,
        options: {
          headers: { "X-Workspace-Id": activeWorkspace?.id },
        },
      },
      {
        onSuccess: (data) => {
          const sanitizedFileName = sanitizeFilename(item.name) + ".csv";

          const fileURL = window.URL.createObjectURL(new Blob([data]));
          const fileLink = document.createElement("a");
          fileLink.href = fileURL;
          fileLink.setAttribute("download", sanitizedFileName);
          document.body.appendChild(fileLink);

          fileLink.click();

          fileLink.remove();
          window.URL.revokeObjectURL(fileURL);
        },
      }
    );
  };

  function sanitizeFilename(name) {
    // Remove invalid characters
    const invalidChars = /[\\/:*?"<>|\x00-\x1F\x80-\x9F]/g; // Regex for invalid characters
    let sanitized = name.replace(invalidChars, "");

    // Prepend 'Enriched-' to the filename
    sanitized = "Enriched-" + sanitized;

    // Truncate the filename if it's too long (200 characters is a common maximum)
    const maxLength = 200;
    sanitized = sanitized.substring(0, maxLength);

    // Avoid filenames that are reserved in Windows
    const reservedNames = [
      "CON",
      "PRN",
      "AUX",
      "NUL",
      "COM1",
      "COM2",
      "COM3",
      "COM4",
      "COM5",
      "COM6",
      "COM7",
      "COM8",
      "COM9",
      "LPT1",
      "LPT2",
      "LPT3",
      "LPT4",
      "LPT5",
      "LPT6",
      "LPT7",
      "LPT8",
      "LPT9",
    ];
    if (reservedNames.includes(sanitized.toUpperCase())) {
      sanitized = "_" + sanitized;
    }

    // Ensure the filename is not empty
    if (sanitized.trim() === "") {
      sanitized = "unnamed_file";
    }

    return sanitized;
  }

  // handle open confirm
  const confirmModalState = (data) => {
    setOpenConfirmModal(data.false);
    if (data && data.confirm && data.type && data.type === "delete") {
      handleDeleteEnrichment();
    }
  };

  // trim .csv extension
  const trimCsvExtension = (filename) => {
    // Check if the filename ends with ".csv" (case-insensitive)
    if (filename.toLowerCase().endsWith(".csv")) {
      // Remove the ".csv" extension
      return filename.slice(0, -4);
    }

    // If the filename doesn't end with ".csv", return it as is
    return filename;
  };

  // Helper function to render status badges
  const returnBadge = (status) => {
    if (status === "Cancelled" || status === "Failed") {
      return <Badge variant={"withdrawn"}>{status}</Badge>;
    } else if (status === "Completed" || status === "Enrichment Completed") {
      return <Badge>{status}</Badge>;
    } else if (
      status === "Finalizing" ||
      status === "Processing" ||
      status === "Retrying" ||
      status === "Pending"
    ) {
      return <Badge variant={"pending"}>{status}</Badge>;
    } else if (status === "No Match" || status === "Paused" || !status) {
      return <Badge variant={"outline"}>{status}</Badge>;
    } else if (status === "Verification Completed") {
      return <Badge variant={"success"}>{status}</Badge>;
    } else {
      return <Badge variant={"outline"}>{status}</Badge>;
    }
  };

  // Handle wizard completion
  const handleWizardComplete = () => {
    setShowWizard(false);
    handleGetEnrichmentList();
  };
  // query for integrations
  const { refetch: refetchIntegrations } = useQuery(
    ["get_integrations", activeWorkspace?.id],
    () =>
      getEnrichmentIntegrations({
        options: {
          headers: {
            "X-Workspace-Id": activeWorkspace?.id,
          },
        },
      }),
    {
      enabled: !!activeWorkspace,
      refetchOnWindowFocus: false,
      staleTime: Infinity, // never mark as stale
      cacheTime: 1000 * 60 * 60 * 24, // 24 hours
      onSuccess: (data) => {
        const installedIntegrations = (data.installed || []).filter((integration) =>
          integration.integration?.capabilities?.includes("csv_contact_enrichment")
        );
        setIntegrations(installedIntegrations);
        setActiveIntegrations(installedIntegrations.map((integration) => integration.id));
        if (data && data.available && data.available.length > 0) {
          const keylogos = data.available.reduce((acc, curr) => {
            acc[curr.key] = curr?.logo;
            return acc;
          }, {});
          setLogos(keylogos);
        }
        if (data && data.installed && data.installed.length === 0) {
          setNoIntegration(true);
        } else {
          setNoIntegration(false);
        }
      },
      onError: (error) => {
        console.error(error);
      },
    }
  );

  //trigger the integration fetch when the workspace changes
  useEffect(() => {
    if (activeWorkspace && activeWorkspace.id) {
      refetchIntegrations();
    }
  }, [activeWorkspace, refetchIntegrations]);

  useEffect(() => {
    // console.log("noIntegration state:", noIntegration);
    // console.log("integrations count:", integrations.length);
  }, [noIntegration, integrations]);

  return (
    <>
      <div className="">
        {!showWizard ? (
          enrichmentList &&
          (enrichmentList.length > 0 ||
            (enrichmentList.length === 0 && !isEmptyObject(filters))) ? (
            <div>
              <PageHeader
                heading="CSV Enrichment"
                description="Enrich a list of People or Companies with a wide range of data points"
                cta={
                  <Button
                    onClick={() => {
                      setShowWizard(true);
                      captureEvent("BLE_UploadCSVBtn");
                    }}
                    type="button"
                  >
                    <LucideUploadCloud className="h-5 w-5 mr-2" aria-hidden="true" />
                    Upload CSV
                  </Button>
                }
              />

              {enrichmentList &&
                (enrichmentList.length > 0 || !isEmptyObject(filters)) && (
                  <div className="mt-7 flex justify-between items-center">
                    <div className="space-x-3 inline-flex items-center">
                      {filterMetadata && (
                        <>
                          <Filter
                            filterMetadata={filterMetadata}
                            getFiltersObject={getFiltersObject}
                          />
                        </>
                      )}
                    </div>
                    <div className="space-x-3">
                      {columnMetadata && (
                        <EditColumn columnMetadata={columnMetadata} getColumns={getColumns} />
                      )}
                    </div>
                  </div>
                )}
              <div>
                {enrichmentList &&
                  enrichmentList.length > 0 &&
                  isSuccess && (
                    <div className="mt-8 flow-root">
                      <div className=" ">
                        <div className=" ">
                          <div className="overflow-hidden overflow-x-auto border border-border rounded-[--radius]">
                            <table className="min-w-full divide-y divide-border">
                              <thead className="bg-background">
                                <tr>
                                  {returnArray(sortedVisibleColumns).map((column, index) => {
                                    return (
                                      <th
                                        key={index}
                                        scope="col"
                                        className={`px-3 py-3.5 text-left text-xs font-medium text-text-600 ${
                                          column.key === "name" ? "w-1/4" : ""
                                        } ${column.sortable ? "cursor-pointer" : ""}`}
                                        onClick={() => (column.sortable ? handleSetSort(column.key) : void 0)}
                                      >
                                        <span className={"inline-flex items-center"}>
                                          {column.display_name}
                                          {column.sortable && (
                                            <>
                                              {sort !== column.key ? (
                                                <ArrowDownIcon width={12} className="inline-block ml-1" />
                                              ) : (
                                                <ArrowUpIcon width={12} className="inline-block ml-1" />
                                              )}
                                            </>
                                          )}
                                        </span>
                                      </th>
                                    );
                                  })}
                                  <th
                                    scope="col"
                                    className="px-3 py-3.5 text-left text-xs font-medium text-text-600 sticky right-0 bg-background"
                                  ></th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-border bg-background">
                                {enrichmentList &&
                                  enrichmentList.length > 0 &&
                                  enrichmentList.map((item, index) => (
                                    <tr key={item.id} className="group">
                                      {returnArray(sortedVisibleColumns).map((column, colIndex) => {
                                        const colKey =
                                          columns &&
                                          Object.keys(columns).find(
                                            (key) => columns[key].order === column.order
                                          );

                                        if (colKey === "created_by") {
                                          return (
                                            <td
                                              key={colIndex}
                                              className="whitespace-nowrap px-3 py-4 text-sm text-text-600"
                                            >
                                              <div className="flex items-center">
                                                <div className="h-9 w-9 flex-shrink-0">
                                                  <Image
                                                    nobaseurl
                                                    className="h-9 w-9 rounded-full"
                                                    src={item[colKey].profile_photo_url}
                                                    alt=""
                                                  />
                                                </div>
                                                <div className="ml-4">
                                                  <div className="font-medium text-text-900">
                                                    {item[colKey].name}
                                                  </div>
                                                  <div className="mt-0 ">{item[colKey].email}</div>
                                                </div>
                                              </div>
                                            </td>
                                          );
                                        }
                                        if (colKey === "name") {
                                          return (
                                            <td
                                              key={colIndex}
                                              className="whitespace-nowrap px-3 py-4 text-sm text-text-900 font-medium w-1/4"
                                              onClick={() => {
                                                captureEvent("BLE_List_ListNameBtn");
                                              }}
                                            >
                                              <div>
                                                {showForm && currentEnrichmentId === item.id ? (
                                                  <EditFileName
                                                    name={trimCsvExtension(item.name)}
                                                    workspaceId={activeWorkspace.id}
                                                    slug={activeWorkspace?.slug}
                                                    listId={item.id}
                                                    hideForm={() => {
                                                      setShowForm(false);
                                                    }}
                                                  />
                                                ) : (
                                                  <span className="inline-flex items-center">
                                                    <NavAnchor
                                                      to={`/enrich/list/${item.id}`}
                                                      className={"text-brand-700"}
                                                    >
                                                      {trimCsvExtension(item.name)}
                                                    </NavAnchor>
                                                    <PencilIcon
                                                      data-tooltip-id="in_table_actions"
                                                      data-tooltip-content="Edit Enrichment List name"
                                                      className="invisible group-hover:visible group-hover:cursor-pointer ml-2"
                                                      width={14}
                                                      height={14}
                                                      onClick={() => {
                                                        setCurrentEnrichmentId(item.id);
                                                        setShowForm(true);
                                                        captureEvent("BLE_List_EditEnrichmentListNameBtn");
                                                      }}
                                                    />
                                                  </span>
                                                )}
                                              </div>
                                            </td>
                                          );
                                        }

                                        if (colKey === "total_records") {
                                          return (
                                            <td
                                              key={colIndex}
                                              className={`whitespace-nowrap px-3 py-4 text-sm  ${
                                                item.total_records !== 0 ? "text-text-600" : "text-text-600"
                                              }`}
                                            >
                                              {item.total_records === 0 ? (
                                                item.total_records
                                              ) : (
                                                <NavAnchor to={`/enrich/list/${item.id}`}>
                                                  {item.total_records}
                                                </NavAnchor>
                                              )}
                                            </td>
                                          );
                                        }

                                        if (colKey === "created_at") {
                                          return (
                                            <td
                                              key={colIndex}
                                              className="whitespace-nowrap px-3 py-4 text-sm text-text-600"
                                            >
                                              <PrintDate date={item.created_at} />
                                            </td>
                                          );
                                        }

                                        if (colKey === "status") {
                                          return (
                                            <td
                                              key={colIndex}
                                              className="whitespace-nowrap px-3 py-4 text-sm text-text-600"
                                            >
                                              {returnBadge(item.status)}
                                            </td>
                                          );
                                        }
                                        if (colKey === "enrichment_mode") {
                                          return (
                                            <td
                                              key={colIndex}
                                              className="whitespace-nowrap px-3 py-4 text-sm text-text-600"
                                            >
                                              {item.enrichment_mode === "Email" && (
                                                <Image src={"/images/icons/untitledui/email-2.svg"} />
                                              )}
                                              {item.enrichment_mode === "Phone" && (
                                                <Image src={"/images/icons/untitledui/phone-2.svg"} />
                                              )}
                                              {(item.enrichment_mode === "Email and Phone" ||
                                                item.enrichment_mode === "Email or Phone") && (
                                                <TooltipWrapper
                                                  className="inline-flex space-x-2 items-center"
                                                  content={item.enrichment_mode}
                                                >
                                                  <Image src={"/images/icons/untitledui/phone-2.svg"} />
                                                  <Image src={"/images/icons/untitledui/email-2.svg"} />
                                                </TooltipWrapper>
                                              )}
                                            </td>
                                          );
                                        }
                                        if (colKey === "requirements") {
                                          return (
                                            <td
                                              key={colIndex}
                                              className="whitespace-nowrap px-3 py-4 text-sm text-text-600"
                                            >
                                              {item?.enrichment_type === "People" && item[colKey] ? (
                                                <>
                                                  {item.requirements === "Email" && (
                                                    <Image src={"/images/icons/untitledui/email-2.svg"} />
                                                  )}
                                                  {item.requirements === "Phone" && (
                                                    <Image src={"/images/icons/untitledui/phone-2.svg"} />
                                                  )}
                                                  {[
                                                    "Email and Phone",
                                                    "Email or Phone",
                                                    "Any Available Data",
                                                  ].includes(item.requirements) && (
                                                    <TooltipWrapper
                                                      className="inline-flex space-x-2 items-center"
                                                      content={item.requirements}
                                                    >
                                                      <Image src={"/images/icons/untitledui/phone-2.svg"} />
                                                      <Image src={"/images/icons/untitledui/email-2.svg"} />
                                                    </TooltipWrapper>
                                                  )}
                                                </>
                                              ) : (
                                                "-"
                                              )}
                                            </td>
                                          );
                                        }

                                        return (
                                          <td
                                            key={colIndex}
                                            className="whitespace-nowrap px-3 py-4 text-sm text-text-600"
                                          >
                                            {item[colKey] ? item[colKey] : "-"}
                                          </td>
                                        );
                                      })}
                                      <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 sticky right-0 bg-background">
                                        {item.status === "Completed" && (
                                          <span className="space-x-1 mr-1">
                                            <Button
                                              data-tooltip-id="in_table_actions"
                                              data-tooltip-content="Download Summarized CSV"
                                              disabled={
                                                downloadSummarizedEnrichListCSVFn.isLoading &&
                                                currentEnrichmentId === item.id
                                              }
                                              onClick={() => {
                                                handleDownloadSummarizedEnrichListCSV(item);
                                                captureEvent("BLE_List_DownloadBtn");
                                              }}
                                              variant="ghost"
                                            >
                                              <ArrowDownTrayIcon width={16} height={16} />
                                              {downloadSummarizedEnrichListCSVFn.isLoading &&
                                                currentEnrichmentId === item.id && (
                                                  <div className="loader_circle ml-2"></div>
                                                )}
                                            </Button>
                                          </span>
                                        )}
                                        <Button
                                          data-tooltip-id="in_table_actions"
                                          data-tooltip-content="Delete List"
                                          disabled={
                                            deleteEnrichmentFn.isLoading && currentEnrichmentId === item.id
                                          }
                                          onClick={(e) => {
                                            e.preventDefault();
                                            setCurrentEnrichmentId(item.id);
                                            setOpenConfirmModal(true);
                                            setOpenConfirmModalType("delete");
                                            captureEvent("BLE_List_DeleteBtn");
                                          }}
                                          variant="ghost"
                                        >
                                          <Image
                                            src="/images/icons/untitledui/trash.svg"
                                            width="16"
                                            height="16"
                                            className="inline-block"
                                          />
                                          {deleteEnrichmentFn.isLoading &&
                                            currentEnrichmentId === item.id && (
                                              <div className="loader_circle ml-2"></div>
                                            )}
                                        </Button>
                                        <Tooltip id="in_table_actions" />
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                {isLoading && <TableLoader />}

                {isSuccess &&
                  enrichmentList &&
                  enrichmentList.length === 0 &&
                  isEmptyObject(filters) && <NoData heading={"No Enrichment List found!"} />}
                {isSuccess &&
                  enrichmentList &&
                  enrichmentList.length === 0 &&
                  !isEmptyObject(filters) && (
                    <div className="p-10 border rounded-lg mt-8">
                      <NoData heading={"No Enrichment List found!"} filterMode={true} />
                    </div>
                  )}
                <div className="flex items-center justify-between  rounded-[--radius] py-3">
                  <div className="flex-1">
                    {paginationInfo && paginationInfo.total > 0 && (
                      <Pagination data={paginationInfo} getPageNumber={getPageNumber} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            enrichmentList &&
            enrichmentList.length === 0 &&
            !isLoading && (
              <>
                {noIntegration && (
                  <AlertTag
                    heading={"Data providers not connected"}
                    description={`You do not have any data providers connected to perform people enrichment. Connect at least one data provider to perform enrichment`}
                    dismiss={false}
                    cta={
                      <NavAnchor to={"/settings/integrations"}>
                        <Button className="h-9">Connect a Provider</Button>
                      </NavAnchor>
                    }
                  />
                )}
                <NoData
                  icon={
                    <div className="rounded-full inline-block bg-smoke">
                      <div className={"bg-brand-100 text-brand-700 rounded-full inline-block p-4"}>
                        <Image src="/images/icons/untitledui/users-purple.svg" width="20" height="20" />
                      </div>
                    </div>
                  }
                  heading={"CSV Enrichment"}
                  description={"Enrich a collection of People or Companies with a wide range of data points"}
                  filterMode={false}
                >
                  <Button
                    onClick={() => {
                      setShowWizard(true);
                      captureEvent("BLE_List_UploadCSVBtn");
                    }}
                    type="button"
                    disabled={noIntegration ? true : false}
                  >
                    <LucideUploadCloud className="h-5 w-5 mr-2" aria-hidden="true" />
                    Upload CSV
                  </Button>
                </NoData>
              </>
            )
          )
        ) : (
          // when showWizard is true - Upload the Wizard
          <UploadWizard onComplete={handleWizardComplete} />
        )}
      </div>

      {isLoading && <TableLoader />}

      <ConfirmModal
        openModal={openConfirmModal}
        modalType={openConfirmModalType}
        confirmModalState={confirmModalState}
      />
    </>
  );
}
